import React from 'react';
import { ArrowLeft, Mail, MessageCircle, Bug, HelpCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export default function Support() {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.getElementById('root')?.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf8f6] to-[#f0ebe5] text-[#3d2c24]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-[#5c4033]/10 px-4 py-3 pt-safe flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2.5 rounded-full hover:bg-stone-200/60 active:bg-stone-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-[#5c4033]" />
        </button>
        <h1 className="text-lg font-semibold text-[#5c4033]" style={{ fontFamily: "'Playfair Display', serif" }}>Support</h1>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-5 py-8 pb-safe space-y-6 leading-relaxed text-base">
        <div>
          <h1 className="text-3xl font-bold text-[#5c4033] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Support</h1>
          <p className="text-[#8b7355] text-sm">We're here to help you get the most out of fashionDisc.</p>
        </div>

        {/* Contact Card */}
        <div className="bg-white/70 border border-[#5c4033]/10 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#cc5500]" />
            <div>
              <p className="font-medium text-[#5c4033]">Email</p>
              <a href="mailto:fashiondisc25@gmail.com" className="text-[#cc5500]">fashiondisc25@gmail.com</a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MessageCircle className="w-5 h-5 text-[#cc5500]" />
            <div>
              <p className="font-medium text-[#5c4033]">Instagram</p>
              <a href="https://instagram.com/fashiondisc.app" target="_blank" rel="noopener noreferrer" className="text-[#cc5500]">@fashiondisc.app</a>
            </div>
          </div>
        </div>

        <div className="bg-[#cc5500]/5 border-l-[3px] border-[#cc5500] rounded-r-lg px-4 py-3">
          <p className="text-sm text-[#5c4033]">We aim to respond to all enquiries within 24–48 hours.</p>
        </div>

        {/* FAQ Section */}
        <Section title="Frequently Asked Questions" icon={<HelpCircle className="w-5 h-5 text-[#cc5500]" />}>
          <FAQ question="How do I manage or cancel my subscription?">
            If you subscribed through the iOS app, go to Settings → Apple ID → Subscriptions on your device. For web subscriptions, you can manage your plan within the app or email us.
          </FAQ>
          <FAQ question="How do I request a refund?">
            For purchases made through Apple, visit <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer" className="text-[#cc5500]">reportaproblem.apple.com</a>. For web purchases, email us at <a href="mailto:fashiondisc25@gmail.com" className="text-[#cc5500]">fashiondisc25@gmail.com</a>.
          </FAQ>
          <FAQ question="How do I delete my account?">
            Go to the Account page in the app and select "Delete Account." This will permanently remove all your data. If you need assistance, email us and we'll handle it for you.
          </FAQ>
          <FAQ question="How do I restore my purchases on a new device?">
            Open the app, go to the subscription or pricing screen, and tap "Restore Purchases." Your active subscription will be restored automatically.
          </FAQ>
          <FAQ question="What AI features does fashionDisc offer?">
            fashionDisc uses AI for virtual try-on, outfit generation, background removal, and personalised style recommendations. All AI processing is handled securely on our servers — your photos are never shared with third parties.
          </FAQ>
        </Section>

        {/* Report a Problem */}
        <Section title="Report a Problem" icon={<Bug className="w-5 h-5 text-[#cc5500]" />}>
          <p>If you're experiencing a bug, crash, or unexpected behaviour, please email us with:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Your device model and iOS version</li>
            <li>A description of what happened</li>
            <li>Steps to reproduce the issue (if possible)</li>
            <li>Screenshots (if applicable)</li>
          </ul>
        </Section>

        {/* Links */}
        <div className="pt-4 border-t border-[#5c4033]/10 flex flex-wrap gap-4 text-sm text-[#8b7355]">
          <Link to="/privacy" className="hover:text-[#cc5500] transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#cc5500] transition-colors">Terms of Service</Link>
          <Link to="/faq" className="hover:text-[#cc5500] transition-colors">Full FAQ</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-xl font-semibold text-[#5c4033]" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h2>
      </div>
      <div className="space-y-3 text-[#5c4033]">{children}</div>
    </div>
  );
}

function FAQ({ question, children }) {
  return (
    <div className="bg-white/50 border border-[#5c4033]/5 rounded-lg p-4">
      <p className="font-medium text-[#5c4033] mb-1">{question}</p>
      <p className="text-sm text-[#5c4033]/80">{children}</p>
    </div>
  );
}
